// GlobalUserSelect.tsx

import React, { useMemo, useState } from 'react'
import { Box, Autocomplete, Avatar, AvatarGroup, FormControl, Grid } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import TypePopperWide from './TypePopperWide'
import RenderInput from './RenderInput'
import RenderOption from './RenderOption'

type UserOption = {
  user_uuid: string
  name: string
  image_512?: string
  role?: string
  firstName?: string
  lastName?: string
}

type Props = {
  name?: string
  value: string[]
  options: UserOption[]
  displayLabel?: string
  onChange: (ids: string[]) => void
  disabled?: boolean
  sx?: object
  isPinned?: boolean
  isHovered?: boolean
  singleSelect?: boolean
  expandedWidth?: number
  context?: string
}

export default function GlobalUserSelect({
  name,
  value = [],
  options = [],
  displayLabel = 'Assignees',
  onChange,
  disabled,
  sx = {},
  isPinned,
  isHovered,
  singleSelect = false,
  expandedWidth = 350,
  context,
}: Props) {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const getId = (u: UserOption) => u.user_uuid

  // IDs -> objects for Autocomplete value
  const selected = useMemo(
    () => {
      if (!value?.length) return []
      const set = new Set(value)
      return options.filter(o => set.has(getId(o)))
    },
    ([options, value] as unknown) as any
  )

  // objects -> IDs for parent
  const handleChange = (_e: any, newOptions: UserOption[]) => {
    const final = singleSelect ? newOptions.slice(-1) : newOptions
    onChange(final.map(getId))
  }

  const handleClear = () => {
    onChange([])
    setOpen(true) // keep menu open after clearing
  }
  const gridSize = open ? 8 : isHovered ? 5.5 : 6
  const prodGridSize = context === 'product' && open ? 8 : isHovered ? 3.5 : 4
  return (
    <Grid
      item
      xs={context === 'product' ? prodGridSize : gridSize}
      sx={t => ({
        transition: t.transitions.create(['flex-basis', 'max-width'], {
          duration: 200,
          easing: t.transitions.easing.easeInOut,
        }),
      })}
    >
      <Box sx={{ position: 'relative', width: '100%', zIndex: open ? 5 : 'auto' }}>
        <FormControl fullWidth>
          <Autocomplete
            multiple={!singleSelect}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={(_, reason) => {
              if ((reason as string) === 'clear') return
              setOpen(false)
            }}
            disableCloseOnSelect={!singleSelect}
            options={options}
            value={selected}
            onChange={handleChange}
            isOptionEqualToValue={(a, b) => getId(a) === getId(b)}
            getOptionLabel={o => o?.name || `${o?.firstName ?? ''} ${o?.lastName ?? ''}`.trim() || 'Unknown User'}
            filterOptions={(opts, { inputValue }) => {
              const q = (inputValue || '').toLowerCase()
              return opts.filter(
                o =>
                  o?.name?.toLowerCase().includes(q) ||
                  o?.role?.toLowerCase().includes(q) ||
                  `${o?.firstName ?? ''} ${o?.lastName ?? ''}`.toLowerCase().includes(q)
              )
            }}
            PopperComponent={TypePopperWide as any}
            componentsProps={{
              popper: {
                widthStrategy: 'match-anchor',
                maxWidth: '90vw',
                zIndex: 2700,
                offset: [0, 2],
              } as any,
              // make the built-in clear "X" clear ids and keep menu open
              clearIndicator: {
                onMouseDown: (e: React.MouseEvent) => {
                  e.preventDefault()
                  e.stopPropagation()
                },
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleClear()
                },
                title: 'Clear assignees',
              },
            }}
            renderOption={(props, option, state) => {
              const { key, id, ...rest } = props
              return (
                <Box
                  component="li"
                  key={`${key}-${id}-${option?.user_uuid}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    width: '100%',
                  }}
                  {...rest}
                >
                  <RenderOption props={props} option={option} theme={theme} />
                </Box>
              )
            }}
            renderTags={(tagValue /* UserOption[] */) => (
              <AvatarGroup
                max={3}
                spacing="small"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '& .MuiAvatar-root': { mr: 0.5, height: 32, width: 32 },
                }}
                slotProps={{
                  additionalAvatar: { sx: { width: 32, height: 32 } },
                }}
              >
                {tagValue.map(u => (
                  <Avatar key={getId(u)} alt={u.name} src={u.image_512} />
                ))}
              </AvatarGroup>
            )}
            renderInput={params => (
              <RenderInput
                params={params}
                displayLabel={displayLabel}
                placeholder={'Assign users'}
                setOpen={setOpen}
                denseHeight
                colors={{
                  color: theme.palette.text.primary,
                  labelColor: theme.palette.text.primary,
                  WebkitTextFillColor: disabled ? theme.palette.text.secondary : theme.palette.text.primary,
                }}
              />
            )}
            disabled={disabled}
            fullWidth
            sx={{
              ...sx,
              '& .MuiInputBase-root': {
                m: 0,
                p: 0,
                pl: 1,
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                bgcolor: (sx as Record<string, any>)?.bgcolor ?? '',
              },
            }}
          />
        </FormControl>
      </Box>
    </Grid>
  )
}
